import LocalizedStrings from "react-localization";
import { useEffect, useState } from "react";
import { Button, Divider, Flex, Grid, Loader } from "@aws-amplify/ui-react";
import toast from "react-hot-toast";
import html2pdf from "html2pdf.js";

import ParametersInfo from "components/parameters/ParametersInfo";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import { API, graphqlOperation } from "aws-amplify";
import { getEquipment, listExecutedServiceOrders } from "graphql/queries";
import EquipmentInfo from "components/equipments/EquipmentInfo";
import ExecutedServiceOrderContainer from "modules/serviceOrders/ExecutedServiceOrder/ExecutedServiceOrderContainer";
import SparesInfo from "components/spares/SparesInfo";

const EquipmentInfoContainer = ({ onSuccess, equipmentID }) => {
  const [executedServiceOrders, setExecutedServiceOrders] = useState({
    data: [],
    loading: false,
  });
  const [equipment, setEquipment] = useState();
  const [loading, setLoading] = useState(false);

  const onExport = async () => {
    try {
      setLoading(true);

      const el = document.querySelector("#export-pdf-equipment");
      const opt = {
        margin: 0.2,
        filename: `equipment-cv.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      };

      await html2pdf().set(opt).from(el).save();

      toast.success(strings.successExported);

      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  async function fetchEquipment(id) {
    try {
      const equipmentData = await API.graphql(
        graphqlOperation(getEquipment, { id })
      );
      const equipmentItem = equipmentData.data.getEquipment;
      setEquipment({
        ...equipmentItem,
      });
      if (equipmentItem.serviceOrders?.items?.length > 0)
        getExecutedServiceOrdersData(
          equipmentItem.serviceOrders.items.map((serv) => serv.serviceOrderId)
        );
    } catch (err) {
      toast.error(strings.responses.errorFetchEquipment);
    }
  }

  const getExecutedServiceOrdersData = async (ids) => {
    try {
      setExecutedServiceOrders({ data: [], loading: true });

      const executedServiceOrdersData = await API.graphql(
        graphqlOperation(listExecutedServiceOrders, {
          filter: {
            or:
              ids?.map((serv) => ({
                serviceOrderID: { eq: serv },
              })) ?? [],
            deletedAt: { attributeExists: false },
            executionStartedAt: { attributeExists: true },
          },
        })
      );
      const executedServiceOrdersItems =
        executedServiceOrdersData.data.listExecutedServiceOrders.items
          ?.sort((a, b) =>
            a.executionStartedAt < b.executionStartedAt ? 1 : -1
          )
          ?.slice(0, 5) ?? [];

      setExecutedServiceOrders((prev) => ({
        ...prev,
        data: executedServiceOrdersItems?.map((ex) => ex.serviceOrderID),
      }));
    } catch (err) {
      toast.error(strings.errors.fetchExecutedServiceOrders);
    } finally {
      setExecutedServiceOrders((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchEquipment(equipmentID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentID]);

  if (!equipment) return <Loader />;

  return (
    <>
      <Grid gap="15px" id="export-pdf-equipment">
        <Grid templateColumns={{ large: "1fr" }} gap="15px">
          <EquipmentInfo equipment={equipment} />
          <ParametersInfo equipmentID={equipment.id} />
          <SparesInfo equipmentID={equipment.id} />
        </Grid>
        {executedServiceOrders.loading ? (
          <Loader />
        ) : (
          <>
            {executedServiceOrders.data.length > 0 && (
              <>
                <Divider className="my-2" />
                <span className="fs-5">{strings.executedServiceOrders}</span>
              </>
            )}
            {executedServiceOrders.data?.map((serviceOrderId, index) => (
              <>
                {index > 0 && <Divider className="my-2" />}
                <ExecutedServiceOrderContainer
                  serviceOrderId={serviceOrderId}
                  viewType="export"
                  fromEquipment
                />
              </>
            ))}
          </>
        )}
        <Flex justifyContent="end" data-html2canvas-ignore>
          <Button
            children={strings.exportCV}
            onClick={onExport}
            variation="primary"
          ></Button>
        </Flex>
      </Grid>
      {loading && <LoaderFullScreen />}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    exportCV: "Exportar Hoja de Vida",
    successExported: "Orden de Servicio exportada con éxito",
    executedServiceOrders: "Ordenes de Servicio ejecutadas",
    responses: {
      unexpectedError: "Ha ocurrido un error inesperado",
      errorFetchEquipment: "Error al consultar el equipo",
    },
  },
});

export default EquipmentInfoContainer;
